import { Component, type ComponentType, type CSSProperties, type ErrorInfo, type ReactNode } from 'react'

/**
 * Contain a render error to the part of the page that threw.
 *
 * Without a boundary, a throw anywhere in a React tree unmounts the whole tree
 * and the user is left on a blank page. Place one at the app root (so nothing
 * is ever blank) and, where an app has chrome that should survive a broken
 * screen (a sidebar, a header), a second one around the screen content only.
 *
 * `resetKey` is what makes the inner placement usable: when it changes, a
 * caught error is cleared and the children render again. Key it on whatever
 * decides which screen is showing (the route pathname in a routed app), so
 * navigating away from a broken screen recovers without a reload.
 *
 * Class component because React still has no hook for catching render errors.
 *
 * Imports nothing else from this package, on purpose: apps mount it in their
 * entry chunk, and pulling in `./primitives` for a Button moved that whole
 * module (every primitive the lazy screens use) onto ops's critical path,
 * +10 kB raw. Import it as `@parkify/ui/error-boundary`, not the barrel.
 */

export interface ErrorFallbackProps {
  error: Error
  /** Clear the error and render the children again. */
  reset: () => void
}

export interface ErrorBoundaryProps {
  children?: ReactNode
  /**
   * Component rendered in place of the children once they throw. Defaults to
   * `ErrorFallback`. It is rendered as an element, so it may use hooks, and it
   * may itself throw to hand the error to the next boundary up (ops does this
   * for stale-chunk errors, which only its root boundary knows how to recover
   * from).
   */
  fallback?: ComponentType<ErrorFallbackProps>
  /** Changing this value clears a caught error. Compared with Object.is. */
  resetKey?: unknown
  /** Replaces the default console.error report. */
  onError?: (error: Error, info: ErrorInfo) => void
  /** Names the boundary in the default console report. */
  label?: string
}

interface ErrorBoundaryState {
  error: Error | null
  resetKey: unknown
}

function toError(thrown: unknown): Error {
  return thrown instanceof Error ? thrown : new Error(String(thrown))
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null, resetKey: this.props.resetKey }

  static getDerivedStateFromError(thrown: unknown): Partial<ErrorBoundaryState> {
    return { error: toError(thrown) }
  }

  static getDerivedStateFromProps(
    props: ErrorBoundaryProps,
    state: ErrorBoundaryState,
  ): Partial<ErrorBoundaryState> | null {
    if (Object.is(props.resetKey, state.resetKey)) return null
    return { resetKey: props.resetKey, error: null }
  }

  componentDidCatch(thrown: unknown, info: ErrorInfo) {
    const error = toError(thrown)
    if (this.props.onError) {
      this.props.onError(error, info)
      return
    }
    console.error(`[ErrorBoundary${this.props.label ? `: ${this.props.label}` : ''}]`, error, info.componentStack)
  }

  reset = () => {
    this.setState({ error: null })
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children
    const Fallback = this.props.fallback ?? ErrorFallback
    return <Fallback error={error} reset={this.reset} />
  }
}

const BUTTON: CSSProperties = {
  padding: '11px 20px', borderRadius: 'var(--radius-btn, 999px)', fontFamily: 'inherit',
  fontSize: 14, fontWeight: 700, cursor: 'pointer',
}
const PRIMARY: CSSProperties = {
  background: 'var(--parkify-accent, var(--color-brand, #7F3F98))', color: 'var(--color-white, #fff)', border: 'none',
}
const QUIET: CSSProperties = {
  background: 'transparent', color: 'var(--parkify-accent, var(--color-brand, #7F3F98))', border: 'none',
}

/**
 * The default fallback: a plain, brand-token card with a reload action. It
 * deliberately does not show `error.message`, because the default is used on
 * customer-facing pages. An operator surface that wants the message (ops does)
 * passes its own `fallback`.
 */
export function ErrorFallback({
  reset,
  title = 'Something went wrong',
  message = 'This page hit an unexpected error. Reloading usually fixes it.',
}: ErrorFallbackProps & { title?: string; message?: string }) {
  return (
    <div
      role="alert"
      style={{
        minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24, boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          width: '100%', maxWidth: 420, padding: '28px 24px', boxSizing: 'border-box',
          background: 'var(--color-white, #fff)', color: 'var(--color-black, #1a1a1a)',
          border: '1px solid var(--color-gray-200, #e5e5e5)', borderRadius: 16,
          boxShadow: 'var(--shadow-sm, 0 4px 16px rgba(0,0,0,0.06))', textAlign: 'center',
        }}
      >
        <h1 style={{ fontSize: 18, fontWeight: 800, margin: '0 0 8px' }}>{title}</h1>
        <p style={{ fontSize: 14, lineHeight: 1.55, margin: '0 0 20px', color: 'var(--color-gray-600, #555)' }}>
          {message}
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button type="button" onClick={() => window.location.reload()} style={{ ...BUTTON, ...PRIMARY }}>
            Reload page
          </button>
          <button type="button" onClick={reset} style={{ ...BUTTON, ...QUIET }}>
            Try again
          </button>
        </div>
      </div>
    </div>
  )
}
